import { useQuery } from "@tanstack/react-query";
import { supabase } from "~/store/initializeStore";
import type { DishFeedback } from "@fitness-app/data-models/entities/DishFeedback";

export type DishFeedbackSortKey = "createdAt" | "rating" | "eventDate" | "email";

export interface DishFeedbackFilters {
  feedbackId?: string;
  traineeId?: string;
  nutritionDayId?: string;
  name?: string;
  email?: string;
  rating?: number;
  sourceKind?: DishFeedback["sourceKind"];
  from?: string;
  to?: string;
  sortBy: DishFeedbackSortKey;
  sortDir: "asc" | "desc";
  page: number;
}

export type DishFeedbackListRow = DishFeedback & {
  trainee: { firstName: string; lastName: string | null; email: string } | null;
};

export const FEEDBACK_PAGE_SIZE = 25;

const escapeLike = (value: string) => value.replace(/[\\%_]/g, "\\$&");

/**
 * The list is paginated, so ordering has to happen in the query — sorting the
 * 25 rows of one page would put the best-rated dish of page 3 behind the
 * worst of page 1. Ordering by the client's email goes through the embedded
 * `trainee` (PostgREST orders the parent by a to-one embed's column).
 *
 * The embed is a left join on purpose: feedback and trainee rows are
 * protected independently (`nutrition.*` scope vs `clients.read`), so an
 * `!inner` join would silently drop feedback the viewer is allowed to see
 * whenever their client scope is narrower. Only the email filter switches to
 * `!inner` — that is the one case where the parent has to be narrowed by the
 * embed, and it can only ever match clients the viewer may read anyway.
 */
export function useDishFeedbackList(filters: DishFeedbackFilters) {
  return useQuery({
    queryKey: ["dish-feedback", "admin", filters],
    queryFn: async () => {
      const offset = (filters.page - 1) * FEEDBACK_PAGE_SIZE;
      const ascending = filters.sortDir === "asc";
      const traineeEmbed = filters.email ? "trainee:traineeId!inner" : "trainee:traineeId";
      const query = supabase
        .from("client_dish_feedback")
        .select(`*, ${traineeEmbed}(firstName, lastName, email)`, { count: "exact" })
        .order(filters.sortBy === "email" ? "trainee(email)" : filters.sortBy, { ascending })
        .range(offset, offset + FEEDBACK_PAGE_SIZE - 1);
      // Equal ratings (or equal days) still come newest first, and the order is stable across pages.
      if (filters.sortBy !== "createdAt") query.order("createdAt", { ascending: false });
      if (filters.feedbackId) query.eq("id", filters.feedbackId);
      if (filters.traineeId) query.eq("traineeId", filters.traineeId);
      if (filters.nutritionDayId) query.eq("nutritionDayId", filters.nutritionDayId);
      if (filters.name) query.ilike("dishName", `%${escapeLike(filters.name)}%`);
      if (filters.email) query.ilike("trainee.email", `%${escapeLike(filters.email)}%`);
      if (filters.rating) query.eq("rating", filters.rating);
      if (filters.sourceKind) query.eq("sourceKind", filters.sourceKind);
      if (filters.from) query.gte("eventDate", filters.from);
      if (filters.to) query.lte("eventDate", filters.to);
      const { data, count, error } = await query.returns<DishFeedbackListRow[]>();
      if (error) throw error;
      return { rows: data, count: count ?? 0 };
    },
  });
}
